package com.example.mystybag.login.views.views

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import kotlinx.coroutines.delay
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.ui.components.custom.CheckIcon
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.login.views.components.MerchantRegistrationProgressBar
import com.example.mystybag.login.views.components.MerchantRegistrationProgressFractions
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.setSoftInputModeOverlay
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import dev.icerock.moko.resources.compose.painterResource
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.login.logic.MerchantRegistrationAddCompanyContactViewModel
import com.example.mystybag.login.model.CompanyContactRequiredField
import com.example.mystybag.common.views.CountryCodePickerBottomSheet

@Composable
fun MerchantRegistrationAddCompanyContactView(viewModel: MerchantRegistrationAddCompanyContactViewModel) {
    DisposableEffect(Unit) {
        setSoftInputModeOverlay(overlay = true)
        onDispose { setSoftInputModeOverlay(overlay = false) }
    }
    LaunchedEffect(Unit) {
        delay(150)
        setSoftInputModeOverlay(overlay = true)
    }

    if (PlatformHelper.isWeb) {
        MerchantRegistrationAddCompanyContactWebView(viewModel)
        return
    }
    BottomSheetUIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        bottomSheetFactory = {}
    ) { _ ->
        MerchantRegistrationAddCompanyContactContent(viewModel)
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyContactContent(
    viewModel: MerchantRegistrationAddCompanyContactViewModel
) {
    val scrollState = rememberScrollState()
    val uiState by viewModel.uiState.subscribeAsState()
    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .keyboardPadding()
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .verticalScroll(scrollState)
            ) {
                MerchantRegistrationAddCompanyContactScrollableBody(viewModel)
            }
            MerchantRegistrationAddCompanyContactFooter(viewModel)
        }

        CountryCodePickerBottomSheet(
            isVisible = uiState.showCompanyPhoneCountryPicker,
            countries = uiState.countries,
            selectedDialCode = uiState.companyDialCode,
            onCountrySelected = { country ->
                viewModel.selectCompanyPhoneCountry(country.id, country.dialCode)
            },
            onDismiss = viewModel::hideCompanyPhoneCountryPicker
        )

        CountryCodePickerBottomSheet(
            isVisible = uiState.showCountryPicker,
            countries = uiState.countries,
            selectedCountryId = uiState.registeredCompanyCountryId,
            onCountrySelected = { country ->
                viewModel.selectRegisteredCompanyCountry(country.id, country.name)
            },
            onDismiss = viewModel::hideCountryPicker
        )
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyContactScrollableBody(
    viewModel: MerchantRegistrationAddCompanyContactViewModel
) {
    val uiState by viewModel.uiState.subscribeAsState()
    MerchantRegistrationProgressBar(MerchantRegistrationProgressFractions.COMPANY_DETAILS)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
    ) {
        Spacer(modifier = Modifier.height(p36))

        Texts.DisplaySmall(
            text = stringResource(SR.strings.company_details),
            modifier = Modifier.fillMaxWidth()
        )

        Spacer(modifier = Modifier.height(p32))

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p24)
        ) {
            DefaultCircleShapedTextField(
                value = uiState.legalEntityName,
                onValueChange = viewModel::updateLegalEntityName,
                label = stringResource(SR.strings.company_name),
                modifier = Modifier.fillMaxWidth(),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                isError = CompanyContactRequiredField.LEGAL_ENTITY_NAME in uiState.requiredFieldErrors,
                errorText = ""
            )

            Box(modifier = Modifier.fillMaxWidth()) {
                DefaultCircleShapedTextField(
                    value = uiState.registeredCompanyCountryName,
                    onValueChange = { },
                    label = stringResource(SR.strings.country),
                    modifier = Modifier.fillMaxWidth(),
                    readOnly = true,
                    enabled = true,
                    canFocus = false,
                    backgroundColor = AppTheme.current.colors.surface,
                    borderColor = AppTheme.current.colors.outline,
                    isError = CompanyContactRequiredField.COUNTRY in uiState.requiredFieldErrors,
                    errorText = "",
                    trailingIcon = {
                        val err = CompanyContactRequiredField.COUNTRY in uiState.requiredFieldErrors
                        Image(
                            painter = painterResource(
                                when {
                                    uiState.showCountryPicker -> SRC.images.combobox_arrow_down_green
                                    uiState.registeredCompanyCountryName.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                    else -> SRC.images.combobox_arrow_right_gray
                                }
                            ),
                            modifier = Modifier.size(p16),
                            colorFilter = if (err) ColorFilter.tint(AppTheme.current.colors.error) else null
                        )
                    }
                )
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .clickable { viewModel.showCountryPicker() }
                )
            }

            Box(modifier = Modifier.fillMaxWidth()) {
                DefaultCircleShapedTextField(
                    value = uiState.registeredCompanyCityName,
                    onValueChange = { },
                    label = stringResource(SR.strings.city),
                    modifier = Modifier.fillMaxWidth(),
                    readOnly = true,
                    enabled = true,
                    canFocus = false,
                    backgroundColor = AppTheme.current.colors.surface,
                    borderColor = AppTheme.current.colors.outline,
                    isError = CompanyContactRequiredField.CITY in uiState.requiredFieldErrors,
                    errorText = "",
                    trailingIcon = {
                        val err = CompanyContactRequiredField.CITY in uiState.requiredFieldErrors
                        Image(
                            painter = painterResource(
                                when {
                                    uiState.showCityPicker -> SRC.images.combobox_arrow_down_green
                                    uiState.registeredCompanyCityName.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                    else -> SRC.images.combobox_arrow_right_gray
                                }
                            ),
                            modifier = Modifier.size(p16),
                            colorFilter = if (err) ColorFilter.tint(AppTheme.current.colors.error) else null
                        )
                    }
                )
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .clickable { viewModel.showCityPicker() }
                )
                DropdownMenu(
                    expanded = uiState.showCityPicker,
                    onDismissRequest = viewModel::hideCityPicker,
                    containerColor = AppTheme.current.colors.surface,
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(20.dp),
                    offset = DpOffset(x = 0.dp, y = 4.dp),
                    modifier = Modifier.heightIn(max = 320.dp)
                ) {
                    uiState.cities.forEachIndexed { index, city ->
                        DropdownMenuItem(
                            text = {
                                Texts.BodyLarge(
                                    text = city.name,
                                    color = if (uiState.registeredCompanyCityId == city.id) AppTheme.current.colors.primary else AppTheme.current.colors.onSurface
                                )
                            },
                            onClick = {
                                viewModel.selectRegisteredCompanyCity(city.id, city.name)
                                viewModel.hideCityPicker()
                            },
                            trailingIcon = {
                                if (uiState.registeredCompanyCityId == city.id) {
                                    CheckIcon(
                                        contentDescription = stringResource(SR.strings.selected),
                                        tint = AppTheme.current.colors.primary
                                    )
                                }
                            }
                        )
                        if (index < uiState.cities.lastIndex) {
                            HorizontalDivider(
                                thickness = 1.dp,
                                color = AppTheme.current.colors.outline
                            )
                        }
                    }
                }
            }

            DefaultCircleShapedTextField(
                value = uiState.registeredCompanyStreet,
                onValueChange = viewModel::updateRegisteredCompanyStreet,
                label = stringResource(SR.strings.address),
                modifier = Modifier.fillMaxWidth(),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                isError = CompanyContactRequiredField.ADDRESS in uiState.requiredFieldErrors,
                errorText = ""
            )

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p12)
            ) {
                Texts.BodyMedium(
                    text = stringResource(SR.strings.your_mobile_number),
                    modifier = Modifier.fillMaxWidth()
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(p8)
                ) {
                    Box(modifier = Modifier.weight(0.32f)) {
                        DefaultCircleShapedTextField(
                            value = uiState.companyDialCode,
                            onValueChange = {},
                            label = stringResource(SR.strings.dial_code),
                            modifier = Modifier.fillMaxWidth(),
                            readOnly = true,
                            enabled = !uiState.isLoading,
                            canFocus = false,
                            backgroundColor = AppTheme.current.colors.surface,
                            borderColor = AppTheme.current.colors.outline,
                            isError = CompanyContactRequiredField.DIAL_CODE in uiState.requiredFieldErrors,
                            errorText = "",
                            trailingIcon = {
                                val err = CompanyContactRequiredField.DIAL_CODE in uiState.requiredFieldErrors
                                Image(
                                    painter = painterResource(
                                        when {
                                            uiState.showCompanyPhoneCountryPicker -> SRC.images.combobox_arrow_down_green
                                            uiState.companyDialCode.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                            else -> SRC.images.combobox_arrow_right_gray
                                        }
                                    ),
                                    modifier = Modifier.size(p16),
                                    colorFilter = if (err) ColorFilter.tint(AppTheme.current.colors.error) else null
                                )
                            }
                        )
                        Box(
                            modifier = Modifier
                                .matchParentSize()
                                .clickable(
                                    enabled = !uiState.isLoading,
                                    onClick = viewModel::showCompanyPhoneCountryPicker
                                )
                        )
                    }
                    DefaultCircleShapedTextField(
                        value = uiState.companyPhoneNumber,
                        onValueChange = viewModel::updateCompanyPhoneNumber,
                        label = stringResource(SR.strings.phone_number_label),
                        modifier = Modifier.weight(0.68f),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                        enabled = !uiState.isLoading,
                        isError = CompanyContactRequiredField.PHONE_NUMBER in uiState.requiredFieldErrors,
                        errorText = ""
                    )
                }

                if (uiState.awaitingSmsVerification) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(p8)
                    ) {
                        Texts.BodyMedium(
                            text = stringResource(
                                SR.strings.merchant_registration_phone_sms_sent,
                                "${uiState.companyDialCode} ${uiState.companyPhoneNumber}"
                            ),
                            modifier = Modifier.fillMaxWidth()
                        )
                        DefaultCircleShapedTextField(
                            value = uiState.smsVerificationCode,
                            onValueChange = viewModel::updateSmsVerificationCode,
                            label = stringResource(SR.strings.sms_verification_code_label),
                            modifier = Modifier.fillMaxWidth(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            enabled = !uiState.isLoading,
                            isError = CompanyContactRequiredField.SMS_CODE in uiState.requiredFieldErrors,
                            errorText = ""
                        )
                        val smsCooldown = uiState.smsResendCooldownSeconds
                        val canResendSms =
                            !uiState.isLoading && smsCooldown == 0
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(p8),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier.clickable(
                                    enabled = canResendSms,
                                    onClick = viewModel::resendPhoneVerificationSms
                                )
                            ) {
                                Texts.BodyMedium(
                                    text = stringResource(SR.strings.resend_sms_code),
                                    color = if (canResendSms) {
                                        AppTheme.current.colors.primary
                                    } else {
                                        AppTheme.current.colors.outline
                                    }
                                )
                            }
                            if (smsCooldown > 0) {
                                Texts.BodyMedium(
                                    text = stringResource(
                                        SR.strings.sms_resend_cooldown_seconds,
                                        smsCooldown
                                    ),
                                    color = AppTheme.current.colors.outline
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun MerchantRegistrationAddCompanyContactFooter(
    viewModel: MerchantRegistrationAddCompanyContactViewModel
) {
    val uiState by viewModel.uiState.subscribeAsState()
    Spacer(modifier = Modifier.height(p32))
    DefaultButton(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = p16, top = p16, end = p16, bottom = p8),
        type = DefaultButtonType.PRIMARY,
        caption = stringResource(SR.strings.next),
        callback = viewModel::onContinue,
        enabled = !uiState.isLoading
    )
}
